import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Modal } from '@ofself/shell';
import { RotateCw, Inbox, Clock, Check, CheckCheck, MessageCircle, X, EyeOff, Archive, AlertTriangle, List } from 'lucide-react';
import { useToast } from '../../components/common';
import NucleusPage, { Verb, FindField } from '../../components/nucleus/NucleusPage';
import Segmented from '../../components/nucleus/Segmented';
import { Row } from '../../components/nucleus/Collection';
import DataTable from '../../components/nucleus/DataTable';
import { TitledSet } from '../../components/nucleus/SetHead';
import { ShowMore } from '../../components/nucleus/Empty';
import Inspector, { Band, Facts, Note, Tags, useOpen } from '../../components/nucleus/Inspector';
import { useRunAction } from '../../components/nucleus/actions';
import { usePlace } from '../../nucleus/place';
import { useSpaceAccess } from '../../nucleus/access';
import NoAccess from '../../components/nucleus/NoAccess';
import { tintOf, textOf } from '../nucleus/GraphModel';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { JsonSheet } from './NodeSheets';
import { useReviewData, PROPOSAL_TABS, QUESTION_TABS, words } from './graphData';
import { NodeMark, QuestionFace } from '../../components/nucleus/faces';
import { ago, plural, fmt } from '../../nucleus/format';

// REVIEW — what waits before it enters the graph: the proposals apps have
// made (approve, edit, later, reject; apply what was approved) and, in your
// personal space, the questions they have asked you. One lane strip runs
// across both — Waiting first, in amber when anything waits, All last: the
// whole record, whatever became of each — and each item opens in the
// inspector with its acts. In a group the proposals are the group's queue;
// questions have no group route, so a group has none.

const P = Object.fromEntries(PROPOSAL_TABS.map((t) => [t.id, t.has]));
const Q = Object.fromEntries(QUESTION_TABS.map((t) => [t.id, t.has]));
const LANES = [
    { id: 'waiting', label: 'Waiting', icon: <Inbox />, p: P.waiting, q: Q.waiting },
    { id: 'later', label: 'Later', icon: <Clock />, p: P.later, q: Q.later },
    { id: 'approved', label: 'Approved', icon: <Check />, p: P.approved },
    { id: 'applied', label: 'Applied', icon: <CheckCheck />, p: P.applied },
    { id: 'failed', label: 'Didn’t apply', icon: <AlertTriangle />, p: P.failed },
    { id: 'answered', label: 'Answered', icon: <MessageCircle />, q: Q.answered },
    { id: 'rejected', label: 'Rejected', icon: <X />, p: P.rejected },
    { id: 'dismissed', label: 'Dismissed', icon: <EyeOff />, q: Q.dismissed },
    { id: 'archived', label: 'Archived', icon: <Archive />, p: P.archived },
    { id: 'all', label: 'All', icon: <List />, p: () => true, q: () => true },
];
// the lane an item stands in by its status; All holds every item, so it names none
const laneOfItem = (x) => LANES.find((l) => l.id !== 'all' && (x.kind === 'proposal' ? l.p : l.q)?.(x.status))?.id || null;
const cap = (s) => String(s).replace(/^./, (c) => c.toUpperCase());
// an item's status in the lane's own word (PENDING reads "Waiting", DEFERRED "Later")
const statusWord = (x) => LANES.find((l) => l.id === laneOfItem(x))?.label || cap(words(x.status.toLowerCase()));
const typeWord = (t) => (t ? words(String(t).toLowerCase()) : null);
const when = (iso) => (iso ? new Date(iso).getTime() : null);

// WHO PROPOSED IT. A plugin run's proposal names its plugin and tool ("From
// Mailroom · Send email") and opens both; an app's names the app. A plugin
// with no tools of its own is its one tool, so its name is said once.
export const pluginOf = (x) => (x?.source?.kind === 'plugin' && x.source.plugin?.id ? x.source : null);
export function fromOf(x) {
    const src = pluginOf(x);
    if (!src) return x?.app || null;
    const plugin = src.plugin.name || x.app || 'A plugin';
    const tool = src.tool?.name;
    return tool && tool !== plugin ? `${plugin} · ${tool}` : plugin;
}
// who answers for it, in a sentence: the plugin, or the app
const whoOf = (x) => pluginOf(x)?.plugin.name || x?.app || null;

// WHY IT DIDN'T APPLY, plainly: the app or plugin that proposed it lost the
// access it had when it asked (apply is all or nothing, so nothing was written).
export function applyRefusal(e, x) {
    const who = whoOf(x) || (pluginOf(x) ? 'The plugin' : 'The app');
    const what = x?.title ? `“${x.title}”` : 'it';
    if (e?.code === 'APP_ACCESS_ENDED') {
        return pluginOf(x)
            ? `${who} can no longer write here, so ${what} wasn’t applied. Nothing was written.`
            : `${who} no longer has access here, so ${what} wasn’t applied. Nothing was written.`;
    }
    if (e?.code === 'APP_ACCESS_NARROWED') return `${who} may no longer ask for this change, so ${what} wasn’t applied. Nothing was written.`;
    return null;
}
const saying = (x, fn) => async () => {
    try { return await fn(); } catch (e) {
        const plain = applyRefusal(e, x);
        if (!plain) throw e;
        const err = new Error(plain);
        err.code = e.code;
        throw err;
    }
};

// A plugin's proposal arrives with a generated line ("Plugin 'Mailroom' proposes
// creating a new node") that only repeats its From; what it says of itself is the rest.
const ownWords = (x) => (x.source?.kind === 'plugin' && /^Plugin '.*' proposes /.test(x.detail || '') ? null : x.detail || null);

export default function ReviewPage() {
    const { groupId: group, spaceName, section, page } = usePlace();
    const toast = useToast();
    const run = useRunAction();
    const review = useReviewData(group);
    const access = useSpaceAccess();
    const [params, setParams] = useSearchParams();
    const [openId, open, close] = useOpen();
    const [find, setFind] = useState('');
    const [sheet, setSheet] = useState(null);           // { kind: edit|reject, item }
    const [drafts, setDrafts] = useState({});

    const lanes = LANES.filter((l) => l.p || !group);
    const laneId = lanes.some((l) => l.id === params.get('lane')) ? params.get('lane') : 'waiting';
    const lane = lanes.find((l) => l.id === laneId);
    const setLane = (id) => setParams((p) => { const n = new URLSearchParams(p); if (id === 'waiting') n.delete('lane'); else n.set('lane', id); n.delete('open'); return n; }, { replace: true });

    const loaded = review.proposals != null && review.questions != null;
    const proposals = review.proposals || [];
    const questions = group ? [] : (review.questions || []);
    const inLane = (l) => [...(l.p ? proposals.filter((x) => l.p(x.status)) : []), ...(l.q ? questions.filter((x) => l.q(x.status)) : [])];
    const shownP = lane.p ? proposals.filter((x) => lane.p(x.status)) : [];
    const shownQ = lane.q ? questions.filter((x) => lane.q(x.status)) : [];
    const q = find.trim().toLowerCase();
    const dim = (x) => !!q && ![x.title, x.text, x.detail, fromOf(x), x.type].filter(Boolean).join(' ').toLowerCase().includes(q);

    // ── acts ──────────────────────────────────────────────────────────────
    // answering one (approve, edit, later, reject, apply) takes the verb it needs
    // on its target: the row's `can_answer`; before that is known, an admin's role
    const answerable = (p) => (!group || access.isAdmin ? true : p.canAnswer ?? false);
    const proposalActs = (p) => {
        const l = laneOfItem(p);
        const may = answerable(p) ? {} : { disabled: true, hint: access.hint };
        if (l === 'waiting' || l === 'later') {
            return [
                {
                    id: 'approve', label: 'Approve', primary: true, ...may,
                    confirm: { title: `Approve “${p.title}”?`, body: `It goes into ${spaceName}’s graph now, as ${whoOf(p) || 'the app'} proposed it. Anything it writes can be trashed later.`, label: 'Approve', danger: false },
                    run: saying(p, async () => toast.success?.(await review.approve(p))),
                },
                { id: 'edit', label: 'Edit', ...may, run: () => setSheet({ kind: 'edit', item: p }) },
                // again from Later too: the third time archives it
                (l === 'waiting' || l === 'later') && { id: 'later', label: 'Later', icon: <Clock />, ...may, run: async () => toast.success?.(await review.later(p)) },
                { id: 'reject', label: 'Reject', danger: true, ...may, run: () => setSheet({ kind: 'reject', item: p }) },
            ].filter(Boolean);
        }
        if (l === 'failed') {
            // nothing of it was written (apply is all or nothing), so trying again is safe;
            // it approves and applies, so it takes what answering does
            return [{ id: 'retry', label: 'Try again', primary: true, icon: <RotateCw />, ...may, run: saying(p, async () => toast.success?.(await review.retry(p))) }];
        }
        if (l === 'approved') {
            return [{
                id: 'apply', label: 'Apply to graph', primary: true, ...may,
                confirm: { title: `Apply “${p.title}”?`, body: `What it proposes is written into ${spaceName}’s graph now.`, label: 'Apply', danger: false },
                run: saying(p, async () => toast.success?.(await review.apply(p))),
            }];
        }
        return [];
    };
    const questionActs = (x) => {
        const l = laneOfItem(x);
        if (l !== 'waiting' && l !== 'later') return [];
        const text = (drafts[x.id] || '').trim();
        return [
            {
                id: 'answer', label: 'Answer', primary: true, disabled: !text, hint: text ? undefined : 'Write an answer, or pick one, first',
                run: async () => { await review.answer(x, text); setDrafts((d) => ({ ...d, [x.id]: '' })); toast.success?.('Answered.'); },
            },
            l === 'waiting' && { id: 'later', label: 'Later', icon: <Clock />, run: async () => { await review.deferQ(x); toast.success?.('For later.'); } },
            { id: 'dismiss', label: 'Dismiss', run: async () => { await review.dismiss(x); toast.success?.('Dismissed.'); } },
        ].filter(Boolean);
    };
    const actsOf = (x) => (x.kind === 'proposal' ? proposalActs(x) : questionActs(x));

    // ── the open item ─────────────────────────────────────────────────────
    const current = proposals.find((x) => x.id === String(openId)) || questions.find((x) => x.id === String(openId)) || null;
    let inspector = null;
    if (current?.kind === 'proposal') {
        inspector = <ProposalInspector key={current.id} p={current} review={review} actions={actsOf(current)} onClose={close} />;
    } else if (current) {
        inspector = (
            <QuestionInspector
                key={current.id}
                x={current}
                draft={drafts[current.id] || ''}
                onDraft={(t) => setDrafts((d) => ({ ...d, [current.id]: t }))}
                onSubmit={() => run(questionActs(current)[0])}
                actions={actsOf(current)}
                onClose={close}
            />
        );
    }

    // the two tables: the item, who it is from, where it stands and when (in All,
    // when it last moved); find dims a row (its first cell carries the mark)
    const atOf = (x) => (laneId === 'all' ? x.updated || x.at : x.at);
    const lead = (label, textOf) => ({
        id: 'item', label, lock: true, min: 220, grow: 3, value: textOf,
        cell: (x) => textOf(x),
    });
    const fromCol = { id: 'from', label: 'From', min: 100, grow: 1, value: fromOf, cell: (x) => fromOf(x) || '—' };
    const statusCol = { id: 'status', label: 'Status', min: 90, grow: 0, value: statusWord, cell: statusWord };
    const whenCol = { id: 'when', label: 'When', min: 96, grow: 0, asc: false, className: 'gx-cell-when', value: (x) => when(atOf(x)),
        cell: (x) => ago(atOf(x), { dateAfter: 7 }) || '—' };
    const proposalColumns = [
        lead('Proposal', (x) => x.title),
        { id: 'kind', label: 'Kind', min: 90, grow: 0.8, value: (x) => typeWord(x.type), cell: (x) => (x.type ? cap(typeWord(x.type)) : '—') },
        fromCol,
        { id: 'about', label: 'About', min: 140, grow: 2, value: (x) => ownWords(x), cell: (x) => ownWords(x) || '—' },
        statusCol, whenCol,
    ];
    const questionColumns = [
        lead('Question', (x) => x.text),
        fromCol,
        { id: 'answer', label: 'Answer', min: 140, grow: 2, value: (x) => x.answer || null,
          cell: (x) => (x.answer ? `“${x.answer}”` : x.options.length ? x.options.join(' / ') : '—') },
        statusCol, whenCol,
    ];
    const table = (id, label, columns, rows) => (
        <DataTable
            id={id} label={label} columns={columns} rows={rows} keyOf={(x) => x.id} dimOf={dim}
            sort={{ id: 'when', dir: 'desc' }} currentKey={openId} onOpen={(id) => open(id)} actionsOf={actsOf}
        />
    );

    const NOTHING = { waiting: 'Nothing is waiting for you.', all: 'No app has proposed anything here yet.' };
    const nothing = NOTHING[laneId] || `Nothing ${lane.label.toLowerCase()}.`;
    return (
        <NucleusPage
            section={section.label}
            title={page.label}
            count={loaded ? shownP.length + shownQ.length : undefined}
            verbs={<>
                <FindField value={find} onChange={setFind} placeholder="Find in review" />
                <Verb icon={<RotateCw />} onClick={() => review.load()}>Refresh</Verb>
            </>}
            inspector={inspector}
        >
            <div className="graph-s-lanes">
                <Segmented
                    label="Lane"
                    options={lanes.map((l) => {
                        const n = loaded ? inLane(l).length : undefined;
                        return { id: l.id, label: l.label, icon: l.icon, count: n != null && n > 99 ? '99+' : n, hot: l.id === 'waiting' && n ? 'amber' : undefined };
                    })}
                    value={laneId}
                    onChange={setLane}
                />
            </div>
            {params.get('done') === '1' && <p className="graph-s-lead is-done" role="status">Done. It’s in your graph.</p>}
            {access.noAccess && <NoAccess />}
            {loaded && !access.noAccess && (
                <div className="nx-shelf" style={{ paddingTop: 24, paddingBottom: 48, gap: 40 }}>
                    {lane.p && (
                        <TitledSet title="Proposals" count={shownP.length} empty={nothing}>
                            {shownP.length > 0 && table('graph-review-proposals', 'Proposals', proposalColumns, shownP)}
                        </TitledSet>
                    )}
                    {lane.q && !group && (shownQ.length > 0 || !lane.p) && (
                        <TitledSet title="Questions" count={shownQ.length} empty={nothing}>
                            {shownQ.length > 0 && table('graph-review-questions', 'Questions', questionColumns, shownQ)}
                        </TitledSet>
                    )}
                </div>
            )}
            {loaded && (
                <ShowMore
                    shown={proposals.length} total={review.total} label="Show more proposals"
                    note={`Read ${fmt(proposals.length)} of ${fmt(review.total)}; the counts above are a floor`}
                    onMore={() => review.more().catch((e) => toast.error?.(e?.message || 'No more came.'))}
                />
            )}

            {sheet?.kind === 'edit' && (
                <ProposalEditor
                    review={review}
                    p={sheet.item}
                    onDone={(msg) => { setSheet(null); if (msg) toast.success?.(msg); }}
                />
            )}
            {sheet?.kind === 'reject' && (
                <RejectSheet
                    p={sheet.item}
                    onClose={() => setSheet(null)}
                    onReject={async (reason) => {
                        await review.reject(sheet.item, reason);
                        setSheet(null);
                        toast.success?.(`Rejected “${sheet.item.title}”.`);
                    }}
                />
            )}
        </NucleusPage>
    );
}

function ProposalInspector({ p, review, actions, onClose }) {
    const { to, navigate } = usePlace();
    const [value, setValue] = useState(null);
    const [changes, setChanges] = useState(null);
    const [json, setJson] = useState(false);
    useEffect(() => {
        let live = true;
        review.read(p)
            .then((r) => { if (live) { setValue(r.value || {}); setChanges(r.changes); } })
            .catch(() => { if (live) setValue({}); });
        return () => { live = false; };
    }, [p.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const keys = Object.keys(value || {}).sort().filter((k) => textOf(value[k]) !== '');
    const tint = tintOf(p.type || p.title);
    const type = typeWord(p.type);
    const from = fromOf(p);
    const src = pluginOf(p);
    return (
        <Inspector
            face={<NodeMark schema={p.type} hue={tint} size={64} />}
            tag={`Proposal · ${statusWord(p)}`}
            title={p.title}
            sub={[from ? `From ${from}` : null, p.at ? ago(p.at, { dateAfter: 7 }) : null].filter(Boolean).join(', ') || undefined}
            actions={actions}
            onClose={onClose}
        >
            <Band title="Where it stands">
                <Facts items={[
                    { label: 'Status', value: statusWord(p), tone: laneOfItem(p) === 'waiting' ? 'warn' : undefined },
                    { label: 'From', value: from || 'An app' },
                    { label: 'Kind', value: type ? cap(type) : '—' },
                    { label: 'Raised', value: ago(p.at, { dateAfter: 7 }) || '—' },
                    p.updated && p.updated !== p.at && { label: 'Last moved', value: ago(p.updated, { dateAfter: 7 }) },
                    p.deferred > 0 && { label: 'Put off', value: plural(p.deferred, 'time') },
                ]} />
                {p.rejection && <Note tone="live">Rejected: {p.rejection}</Note>}
            </Band>
            {changes && (
                <Band title="What it changes" aside={plural(changes.length, 'field')}>
                    {changes.length === 0 && <Note>Nothing: the node already reads this way.</Note>}
                    <ChangeList changes={changes} />
                </Band>
            )}
            {value && !changes && (
                <Band title="What it would write" aside={<button type="button" className="nx-link" onClick={() => setJson(true)}>JSON</button>}>
                    {keys.length === 0 && <Note>No fields to show.</Note>}
                    {keys.length > 0 && (
                        <dl className="graph-s-fields">
                            {keys.map((k) => <div key={k} className="graph-s-field"><dt>{words(k)}</dt><dd>{textOf(value[k])}</dd></div>)}
                        </dl>
                    )}
                </Band>
            )}
            {p.steering && <Band title="It asks"><Note>{p.steering}</Note></Band>}
            {(ownWords(p) || !src) && <Band title="About"><Note>{ownWords(p) || 'It says nothing more about itself.'}</Note></Band>}
            {(p.appId || src || p.rootNode || p.tags.length > 0) && (
                <Band title="Related">
                    {src && (
                        <Row name={src.plugin.name || 'The plugin'} sub="The plugin that proposed it"
                            onPress={() => navigate(to('plugins', null, `open=${encodeURIComponent(src.plugin.id)}`))} />
                    )}
                    {src?.run_id && (
                        <Row name="The run" sub={src.tool?.name && src.tool.name !== src.plugin.name ? `${src.tool.name}, the run that proposed it` : 'The run that proposed it'}
                            onPress={() => navigate(to('plugins', 'runs', `open=${encodeURIComponent(src.run_id)}`))} />
                    )}
                    {!src && p.appId && (
                        <Row name={p.app || 'The app'} sub="The app that proposed it"
                            onPress={() => navigate(to('apps', null, `open=${encodeURIComponent(p.appId)}`))} />
                    )}
                    {p.rootNode && (
                        <Row name="The node it starts from" sub={p.rootNode}
                            onPress={() => navigate(to('graph', null, `open=${encodeURIComponent(p.rootNode)}`))} />
                    )}
                    <Tags items={p.tags.map((t) => ({ key: t, label: t }))} />
                </Band>
            )}
            {json && <JsonSheet title={`${p.title} · JSON`} value={value} onClose={() => setJson(false)} />}
        </Inspector>
    );
}

function QuestionInspector({ x, draft, onDraft, onSubmit, actions, onClose }) {
    const openLane = ['waiting', 'later'].includes(laneOfItem(x));
    return (
        <Inspector
            face={<QuestionFace size={64} />}
            tag={['Question', statusWord(x)].join(' · ')}
            title={x.text}
            sub={[x.app ? `From ${x.app}` : null, x.at ? `asked ${ago(x.at, { dateAfter: 7 })}` : null].filter(Boolean).join(', ') || undefined}
            actions={actions}
            onClose={onClose}
        >
            <Band title="Where it stands">
                <Facts items={[
                    { label: 'Status', value: statusWord(x), tone: laneOfItem(x) === 'waiting' ? 'warn' : undefined },
                    { label: 'Asked', value: ago(x.at, { dateAfter: 7 }) || '—' },
                    x.app && { label: 'From', value: x.app },
                    x.type && { label: 'Kind', value: cap(typeWord(x.type)) },
                ]} />
            </Band>
            {openLane ? (
                <Band title="Your answer" aside={x.options.length ? plural(x.options.length, 'choice') : null}>
                    {x.options.length > 0 && <Tags items={x.options.map((o) => ({ key: o, label: o, tone: draft === o ? 'strong' : undefined, onPress: () => onDraft(o) }))} />}
                    <textarea
                        className="graph-s-answer"
                        rows={3}
                        placeholder={x.options.length ? 'Pick one, or say it in your own words' : 'Your answer'}
                        aria-label="Your answer"
                        value={draft}
                        onChange={(e) => onDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && draft.trim()) { e.preventDefault(); onSubmit(); } }}
                    />
                    <Note>Enter answers. Shift-Enter starts a new line.</Note>
                </Band>
            ) : (
                <Band title="Your answer"><Note>{x.answer || (x.status === 'DISMISSED' ? 'You dismissed it.' : '—')}</Note></Band>
            )}
        </Inspector>
    );
}

function ProposalEditor({ review, p, onDone }) {
    const [title, setTitle] = useState(p.title);
    const [detail, setDetail] = useState(p.detail || '');
    const [json, setJson] = useState('{}');
    const [shown, setShown] = useState({});
    const [error, setError] = useState(null);
    const [working, setWorking] = useState(false);
    useEffect(() => { review.detail(p).then((v) => { setShown(v || {}); setJson(JSON.stringify(v || {}, null, 2)); }).catch(() => {}); }, [p.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const save = async () => {
        let value;
        try { value = JSON.parse(json || '{}') || {}; } catch { setError('The value isn’t valid JSON.'); return; }
        setWorking(true);
        setError(null);
        try { onDone(await review.editConfirm(p, { title, detail, value, shown })); } catch (e) { setError(applyRefusal(e, p) || e?.message || 'It didn’t go in.'); setWorking(false); }
    };
    return (
        <Modal title="Edit before it goes in" lead="What you confirm here is written to the graph as it stands." onClose={() => onDone(null)}>
            <div className="graph-sheet">
                <input className="graph-input is-title" placeholder="Title" aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
                <input className="graph-input" placeholder="Description" aria-label="Description" value={detail} onChange={(e) => setDetail(e.target.value)} />
                <textarea className="graph-input is-mono graph-code" rows={10} value={json} onChange={(e) => setJson(e.target.value)} aria-label="Value JSON" />
                {error && <p className="graph-sheet__error" role="alert">{error}</p>}
                <SheetFoot>
                    <FootButton onClick={() => onDone(null)}>Cancel</FootButton>
                    <FootButton primary busy={working} onClick={save}>Confirm and apply</FootButton>
                </SheetFoot>
            </div>
        </Modal>
    );
}

function RejectSheet({ p, onReject, onClose }) {
    const [reason, setReason] = useState('');
    const [error, setError] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        try { await onReject(reason); } catch (err) { setError(err?.message || 'It wasn’t rejected.'); }
    };
    return (
        <Modal title={`Reject “${p.title}”?`} lead="Nothing of it enters the graph. The app hears why, if you say." onClose={onClose} size="md">
            <form className="graph-sheet" onSubmit={submit}>
                <input autoFocus className="graph-input" placeholder="Why (optional)" aria-label="Why" value={reason} onChange={(e) => setReason(e.target.value)} />
                {error && <p className="graph-sheet__error" role="alert">{error}</p>}
                <SheetFoot>
                    <FootButton onClick={onClose}>Cancel</FootButton>
                    <FootButton danger type="submit">Reject</FootButton>
                </SheetFoot>
            </form>
        </Modal>
    );
}

// An update's changes, old -> new, one field to a line.
export function ChangeList({ changes }) {
    if (!changes?.length) return null;
    const shown = (v) => (v === null || v === undefined || v === '' ? '—' : textOf(v));
    return (
        <dl className="graph-s-fields">
            {changes.map((c) => (
                <div key={c.field} className="graph-s-field">
                    <dt>{words(c.field)}</dt>
                    <dd><s>{shown(c.old)}</s> <span aria-hidden="true">→</span> <b>{shown(c.new)}</b></dd>
                </div>
            ))}
        </dl>
    );
}
